import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Optional } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpRequest } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import * as Utils from 'common-utils/dist/csv-export';
import { Subject } from 'rxjs';

const NOC_ANALYTICS = new InjectionToken('NOC_ANALYTICS');
class AnalyticsService {
    constructor(analytics) {
        this.analytics = analytics;
        this.eventQueue = [];
    }
    init(userId, applicationName) {
        if (this.eventQueue) {
            this.analytics.init(userId, applicationName);
            this.eventQueue.forEach((event) => {
                switch (event.type) {
                    case 'PushEvent': {
                        const { eventName, properties, eventPage } = event;
                        this.analytics.pushEvent(eventName, properties, eventPage);
                        break;
                    }
                    case 'PageEvent': {
                        const { pageName } = event;
                        this.analytics.setPage(pageName);
                        break;
                    }
                }
            });
            delete this.eventQueue;
        }
        else {
            console.warn('Analytics Service has already been initialised');
        }
    }
    get mixpanel() {
        return this.analytics.getMixpanel();
    }
    pushEvent(eventName, properties, eventPage) {
        if (!this.eventQueue) {
            this.analytics.pushEvent(eventName, properties, eventPage);
        }
        else {
            this.eventQueue.push(this.PushEvent(eventName, properties, eventPage));
        }
    }
    setPage(pageName) {
        if (!this.eventQueue) {
            this.analytics.setPage(pageName);
        }
        else {
            this.eventQueue.push(this.PageEvent(pageName));
        }
    }
    PushEvent(eventName, properties, eventPage) {
        const type = 'PushEvent';
        return { type, eventName, properties, eventPage };
    }
    PageEvent(pageName) {
        const type = 'PageEvent';
        return { type, pageName };
    }
}
AnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: AnalyticsService, deps: [{ token: NOC_ANALYTICS }], target: i0.ɵɵFactoryTarget.Injectable });
AnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: AnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: AnalyticsService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NOC_ANALYTICS]
                }] }]; } });

/**
 * @dynamic
 * See https://github.com/angular/angular/issues/20351#issuecomment-344009887
 */
class CookieService {
    getWindow() {
        // return the global native browser window object
        return window;
    }
    get cookies() {
        return this.getWindow().document.cookie.split(';')
            .map((pair) => pair.split('=').map((token) => token.trim()))
            .reduce((a, [key, value]) => { a[key] = value; return a; }, {});
    }
    /**
     * Gets raw locale from the cookie
     */
    getRawI18nLocale() {
        return this.getCookie('locale') || '';
    }
    /**
     * Gets the locale cookie formatted for i18n
     */
    getI18NLocale() {
        const raw = this.getCookie('locale') || '';
        return raw.replace('_', '-').toLowerCase();
    }
    /**
     * Gets the brand value formatted for i18n
     */
    getI18NBrand() {
        const raw = this.getCookie('default_brand') || '';
        return raw.toUpperCase();
    }
    /**
     * Gets any cookie from the application
     */
    getCookie(cname) {
        return this.cookies[cname];
    }
    setCookie(cookieName, cookieValue, options = {}) {
        const entries = Object.entries(options);
        const cookie = entries.reduce((a, [optionKey, optionValue]) => `${a};${optionKey}=${optionValue}`, `${cookieName}=${encodeURIComponent(cookieValue)}`);
        this.getWindow().document.cookie = cookie;
    }
}
CookieService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: CookieService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CookieService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: CookieService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: CookieService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class LoggerService {
    constructor(http, loggerUrl) {
        this.http = http;
        this.loggerUrl = loggerUrl;
    }
    /**
     * Use this function when it is not possible to configure the `loggerUrl` through DI
     */
    endPoint(endpoint) {
        this.loggerUrl = endpoint;
        return this;
    }
    /**
     * @param <T> the type of the payload to be sent to the logs api
     * @param <U> the type of answer we expect to receive from the logs api
     */
    log(payload) {
        const headers = new HttpHeaders();
        headers.append('Content-Type', 'application/json');
        const httpRequest = new HttpRequest('POST', this.loggerUrl, payload, { headers, withCredentials: true });
        return this.http.request(httpRequest);
    }
}
LoggerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: LoggerService, deps: [{ token: i1.HttpClient }, { token: 'loggerUrl', optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
LoggerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: LoggerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: LoggerService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['loggerUrl']
                }] }]; } });

/**
 * @dynamic
 * See https://github.com/angular/angular/issues/20351#issuecomment-344009887
 */
class DomainAwarenessService {
    constructor(document) {
        this.document = document;
        this.brand = this.getBrandFromDomain();
        this.namespace = this.getNamespaceFromDomain();
    }
    /**
     * This should only get used to switch the set of APIs to use. Nothing to do with styling.
     * See https://rally1.rallydev.com/#/227989437820d/detail/userstory/248698385148
     */
    getBrandFromDomain() {
        const { domain } = this.document;
        return ['demo', 'consumersenergy', 'enelx'].reduce((a, b) => (domain.includes(b)) ? b : a, undefined);
    }
    /**
     * This should work for all deployed applications provided they follow the naming convention in use.
     * If you're seeing errors/warnings when running this locally, make sure you're serving the app with
     * an appropriate host (e.g `ng serve --host foo.bar.baz` where `foo.bar.baz` maps to an entry in your
     * `/etc/hosts` file). Since this is based on naming convention, this isn't bulletproof and will likely
     * be subject to change.
     *
     * Here is a breakdown of the regex `/(.*?)\.(.*?)\.(.*)/`:
     *  - The first 2 non-greedy capture groups are delimited but the `.` character.
     *  - Leftmost non-greedy capture group should be the application name.
     *  - Second non-greedy capture group should be the backend environment.
     *  - Tail should be the leftover domain name (usually `enelx.net` or `enernoc.net`)
     */
    getNamespaceFromDomain() {
        try {
            const [, application, environment, domain] = /(.*?)\.(.*?)\.(.*)/.exec(this.document.domain);
            return { application, environment, domain };
        }
        catch (e) {
            console.warn('Could not parse namespace from domain');
            return undefined;
        }
    }
}
DomainAwarenessService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: DomainAwarenessService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
DomainAwarenessService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: DomainAwarenessService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: DomainAwarenessService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class CsvExporterService {
    export(rows, fileName) {
        Utils.exportFile(Utils.serialise(rows), fileName);
    }
    import(blob) {
        let subject = new Subject();
        Utils.importFile(blob).subscribe((rows) => subject.next(rows));
        return subject.asObservable();
    }
}
CsvExporterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: CsvExporterService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CsvExporterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: CsvExporterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: CsvExporterService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsService, CookieService, CsvExporterService, DomainAwarenessService, LoggerService, NOC_ANALYTICS };
